<template>
  <v-container>
    <v-row>
      <v-col cols="12">
        <v-card>
          <v-card-title class="d-flex flex-wrap align-center gap-3">
            <span>Dashboard financiero</span>
            <v-spacer></v-spacer>
            <div class="d-flex flex-wrap gap-3">
              <v-text-field
                v-model="fechaInicio"
                type="date"
                label="Fecha inicio"
                density="compact"
                hide-details
                style="min-width: 160px"
              ></v-text-field>
              <v-text-field
                v-model="fechaFin"
                type="date"
                label="Fecha fin"
                density="compact"
                hide-details
                style="min-width: 160px"
              ></v-text-field>
              <v-btn color="primary" :loading="loading" @click="cargarPagos">
                Actualizar
              </v-btn>
            </div>
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text>
            <v-alert v-if="error" type="error" variant="tonal" class="mb-4">
              {{ error }}
            </v-alert>
            <v-row class="mb-4">
              <v-col cols="12" md="3">
                <v-card variant="tonal" color="primary">
                  <v-card-title class="text-subtitle-1">Total pagos</v-card-title>
                  <v-card-text class="text-h5 font-weight-bold">
                    {{ formatCurrency(totalPagos) }}
                  </v-card-text>
                  <v-card-subtitle class="text-caption text-medium-emphasis">
                    {{ cantidadPagos }} {{ cantidadPagos === 1 ? 'pago' : 'pagos' }}
                  </v-card-subtitle>
                </v-card>
              </v-col>
              <v-col cols="12" md="3">
                <v-card variant="tonal" color="error">
                  <v-card-title class="text-subtitle-1">Total gastos</v-card-title>
                  <v-card-text class="text-h5 font-weight-bold">
                    {{ formatCurrency(totalGastos) }}
                  </v-card-text>
                  <v-card-subtitle class="text-caption text-medium-emphasis">
                    {{ cantidadGastos }} {{ cantidadGastos === 1 ? 'gasto' : 'gastos' }}
                    · Utilidad: {{ formatCurrency(utilidad) }}
                  </v-card-subtitle>
                </v-card>
              </v-col>
              <v-col cols="12" md="3">
                <v-card variant="outlined">
                  <v-card-title class="text-subtitle-1">Total matrículas</v-card-title>
                  <v-card-text class="text-h5 font-weight-bold">
                    {{ formatCurrency(totalMatriculas) }}
                  </v-card-text>
                  <v-card-subtitle class="text-caption text-medium-emphasis">
                    {{ cantidadMatriculas }} {{ cantidadMatriculas === 1 ? 'matrícula' : 'matrículas' }}
                  </v-card-subtitle>
                </v-card>
              </v-col>
              <v-col cols="12" md="3">
                <v-card variant="outlined">
                  <v-card-title class="text-subtitle-1">Relación pagos / matrículas</v-card-title>
                  <v-card-text>
                    <div ref="resumenChart" class="gauge-chart"></div>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
            <v-card variant="outlined">
              <v-card-title class="text-subtitle-1">Pagos vs Gastos por mes</v-card-title>
              <v-card-text>
                <div v-if="detallePorMes.length" class="chart-panel">
                  <div ref="pagosChart" class="amchart"></div>
                </div>
                <div v-else class="text-caption text-grey">
                  No hay datos para el rango seleccionado.
                </div>
              </v-card-text>
            </v-card>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { defineComponent, ref, onMounted, onBeforeUnmount, watch, nextTick, computed } from 'vue'
import axios from 'axios'
import * as am5 from '@amcharts/amcharts5'
import * as am5xy from '@amcharts/amcharts5/xy'
import * as am5radar from '@amcharts/amcharts5/radar'
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated'
import { useTheme } from 'vuetify'

const formatDateInput = (date) => {
  const year = date.getFullYear()
  const month = `${date.getMonth() + 1}`.padStart(2, '0')
  const day = `${date.getDate()}`.padStart(2, '0')
  return `${year}-${month}-${day}`
}

export default defineComponent({
  setup() {
    const today = new Date()
    const pagosPorMes = ref([])
    const detallePorMes = ref([])
    const totalPagos = ref(0)
    const totalGastos = ref(0)
    const totalMatriculas = ref(0)
    const cantidadPagos = ref(0)
    const cantidadGastos = ref(0)
    const cantidadMatriculas = ref(0)
    const utilidad = ref(0)
    const fechaInicio = ref(formatDateInput(new Date(today.getFullYear(), today.getMonth(), 1)))
    const fechaFin = ref(formatDateInput(today))
    const loading = ref(false)
    const error = ref('')
    const chartRoot = ref(null)
    const chartSeries = ref(null)
    const chartAxes = ref({ xAxis: null, yAxis: null })
    const pagosChart = ref(null)
    const resumenRoot = ref(null)
    const resumenChart = ref(null)
    const theme = useTheme()
    const isDark = computed(() => theme.global.current.value.dark)

    const cargarPagos = async () => {
      loading.value = true
      error.value = ''
      try {
        const response = await axios.get('/api/dashboard/financiero/pagos-por-mes', {
          params: {
            fecha_inicio: fechaInicio.value || undefined,
            fecha_fin: fechaFin.value || undefined,
          },
        })
        detallePorMes.value = (response.data.detalle || []).map((item) => ({
          ...item,
          total_pagos: Number(item.total_pagos) || 0,
          total_gastos: Number(item.total_gastos) || 0,
        }))

        const ordenado = [...detallePorMes.value]
          .sort((a, b) => a.anio - b.anio || a.mes - b.mes)
          .map((item) => ({
            ...item,
            categoria: `${String(item.mes).padStart(2, '0')}/${item.anio}`,
          }))
        pagosPorMes.value = ordenado

        totalPagos.value = Number(response.data.total_pagos) || 0
        totalGastos.value = Number(response.data.total_gastos) || 0

        await cargarResumen()
      } catch (err) {
        console.error('Error cargando pagos', err)
        error.value = 'No se pudo cargar la información financiera. Intenta nuevamente.'
      } finally {
        loading.value = false
      }
    }

    const cargarResumen = async () => {
      try {
        const response = await axios.get('/api/dashboard/financiero/resumen', {
          params: {
            fecha_inicio: fechaInicio.value,
            fecha_fin: fechaFin.value,
          },
        })
        const pagos = Number(response.data.total_pagos) || 0
        const matriculas = Number(response.data.total_matriculas) || 0
        cantidadPagos.value = Number(response.data.cantidad_pagos) || 0
        cantidadMatriculas.value = Number(response.data.cantidad_matriculas) || 0
        cantidadGastos.value = Number(response.data.cantidad_gastos) || 0
        utilidad.value = Number(response.data.utilidad) || 0
        const ratio = matriculas > 0 ? Math.min(150, (pagos / matriculas) * 100) : 0
        updateGauge(ratio)
      } catch (err) {
        console.error('Error cargando resumen', err)
        // no bloquear flujo principal
      }
    }

    const applyChartTheme = () => {
      const root = chartRoot.value
      const { xAxis, yAxis } = chartAxes.value
      if (!root || !xAxis || !yAxis) return

      const textColor = isDark.value ? am5.color(0xffffff) : am5.color(0x424242)
      const gridColor = isDark.value ? am5.color(0x4b5563) : am5.color(0xe0e0e0)
      const backgroundColor = isDark.value ? am5.color(0x121212) : am5.color(0xffffff)

      xAxis.get('renderer').labels.template.setAll({ fill: textColor })
      yAxis.get('renderer').labels.template.setAll({ fill: textColor })
      xAxis.get('renderer').grid.template.setAll({ stroke: gridColor, strokeOpacity: 0.6 })
      yAxis.get('renderer').grid.template.setAll({ stroke: gridColor, strokeOpacity: 0.6 })

      root.container.set('background', am5.Rectangle.new(root, { fill: backgroundColor }))
    }

    const initChart = async () => {
      await nextTick()
      if (chartRoot.value) return
      if (!(pagosChart.value instanceof HTMLElement)) return

      const root = am5.Root.new(pagosChart.value)
      chartRoot.value = root
      root.setThemes([am5themes_Animated.new(root)])

      const chart = root.container.children.push(
        am5xy.XYChart.new(root, {
          panX: false,
          panY: false,
          paddingLeft: 0,
          wheelX: 'panX',
          wheelY: 'zoomX',
          layout: root.verticalLayout,
        })
      )

      const legend = chart.children.push(
        am5.Legend.new(root, {
          centerX: am5.p50,
          x: am5.p50,
        })
      )

      const xRenderer = am5xy.AxisRendererX.new(root, {
        cellStartLocation: 0.1,
        cellEndLocation: 0.9,
        minorGridEnabled: true,
      })

      const xAxis = chart.xAxes.push(
        am5xy.CategoryAxis.new(root, {
          categoryField: 'categoria',
          renderer: xRenderer,
          tooltip: am5.Tooltip.new(root, {}),
        })
      )

      xRenderer.grid.template.setAll({ location: 1 })

      const yAxis = chart.yAxes.push(
        am5xy.ValueAxis.new(root, {
          renderer: am5xy.AxisRendererY.new(root, { strokeOpacity: 0.1 }),
        })
      )
      chartAxes.value = { xAxis, yAxis }

      const makeSeries = (name, fieldName, color) => {
        const series = chart.series.push(
          am5xy.ColumnSeries.new(root, {
            name,
            xAxis,
            yAxis,
            valueYField: fieldName,
            categoryXField: 'categoria',
            fill: color,
            stroke: color,
          })
        )

        series.columns.template.setAll({
          tooltipText: '{name}, {categoryX}: S/ {valueY.formatNumber("#,###.00")}',
          width: am5.percent(90),
          tooltipY: 0,
          strokeOpacity: 0,
        })

        series.appear()

        series.bullets.push(() => {
          return am5.Bullet.new(root, {
            sprite: am5.Label.new(root, {
              text: '{valueY.formatNumber("#,###")}',
              fill: root.interfaceColors.get('alternativeText'),
              centerY: am5.p50,
              centerX: am5.p50,
              populateText: true,
            }),
          })
        })

        legend.data.push(series)
        return series
      }

      const pagosSeries = makeSeries('Pagos', 'total_pagos', am5.color(0xf2cd14))
      const gastosSeries = makeSeries('Gastos', 'total_gastos', am5.color(0xc62828))

      chartSeries.value = [pagosSeries, gastosSeries]

      applyChartTheme()
      chart.appear(1000, 100)
    }

    const updateChartData = async () => {
      if (!pagosPorMes.value.length) {
        if (Array.isArray(chartSeries.value)) {
          chartSeries.value.forEach((s) => s.data.setAll([]))
        }
        return
      }

      await initChart()
      if (!Array.isArray(chartSeries.value) || !chartAxes.value.xAxis) return

      const data = pagosPorMes.value.map((item) => ({
        categoria: item.categoria,
        total_pagos: item.total_pagos,
        total_gastos: item.total_gastos,
      }))

      chartAxes.value.xAxis.data.setAll(data)
      chartSeries.value.forEach((series) => {
        series.data.setAll(data)
      })
      applyChartTheme()
    }

    watch(
      pagosPorMes,
      () => {
        updateChartData()
      },
      { flush: 'post' }
    )

    watch(
      isDark,
      () => {
        applyChartTheme()
      },
      { flush: 'post' }
    )

    onMounted(() => {
      cargarPagos()
      initChart()
    })

  onBeforeUnmount(() => {
    if (chartRoot.value) {
      chartRoot.value.dispose()
      chartRoot.value = null
      chartSeries.value = null
      chartAxes.value = { xAxis: null, yAxis: null }
    }
    if (resumenRoot.value) {
      resumenRoot.value.dispose()
      resumenRoot.value = null
      resumenChart.value = null
    }
  })

  const formatCurrency = (value) => {
    const number = Number(value) || 0
    return number.toLocaleString('es-PE', { style: 'currency', currency: 'PEN' })
  }

  const initGauge = async () => {
    await nextTick()
    if (resumenRoot.value) return
    if (!(resumenChart.value instanceof HTMLElement)) return

    const root = am5.Root.new(resumenChart.value)
    resumenRoot.value = root
    root.setThemes([am5themes_Animated.new(root)])

    const chart = root.container.children.push(
      am5radar.RadarChart.new(root, {
        panX: false,
        panY: false,
        startAngle: 180,
        endAngle: 360,
      })
    )

    const axisRenderer = am5radar.AxisRendererCircular.new(root, {
      innerRadius: -40,
    })
    axisRenderer.grid.template.setAll({
      stroke: root.interfaceColors.get('background'),
      visible: true,
      strokeOpacity: 0.8,
    })

    const xAxis = chart.xAxes.push(
      am5xy.ValueAxis.new(root, {
        maxDeviation: 0,
        min: 0,
        max: 150,
        strictMinMax: true,
        renderer: axisRenderer,
      })
    )

    const axisDataItem = xAxis.makeDataItem({})

    const clockHand = am5radar.ClockHand.new(root, {
      pinRadius: 40,
      radius: am5.percent(100),
      innerRadius: 40,
      bottomWidth: 0,
      topWidth: 0,
    })

    clockHand.pin.setAll({
      fillOpacity: 0,
      strokeOpacity: 0.5,
      stroke: am5.color(0x000000),
      strokeWidth: 1,
      strokeDasharray: [2, 2],
    })
    clockHand.hand.setAll({
      fillOpacity: 0,
      strokeOpacity: 0.5,
      stroke: am5.color(0x000000),
      strokeWidth: 0.5,
    })

    const bullet = axisDataItem.set(
      'bullet',
      am5xy.AxisBullet.new(root, {
        sprite: clockHand,
      })
    )

    xAxis.createAxisRange(axisDataItem)

    const label = chart.radarContainer.children.push(
      am5.Label.new(root, {
        centerX: am5.percent(50),
        textAlign: 'center',
        centerY: am5.percent(50),
        fontSize: '1.25rem',
        fontWeight: 'bold',
      })
    )

    const colorSet = am5.ColorSet.new(root, {})

    const axisRange0 = xAxis.createAxisRange(
      xAxis.makeDataItem({
        above: true,
        value: 0,
        endValue: 100,
      })
    )
    axisRange0.get('axisFill').setAll({
      visible: true,
      fill: colorSet.getIndex(0),
      fillOpacity: 0.4,
    })

    const axisRange1 = xAxis.createAxisRange(
      xAxis.makeDataItem({
        above: true,
        value: 100,
        endValue: 150,
      })
    )
    axisRange1.get('axisFill').setAll({
      visible: true,
      fill: colorSet.getIndex(4),
      fillOpacity: 0.4,
    })

    const updateLabel = () => {
      const value = axisDataItem.get('value') ?? 0
      label.set('text', `${Math.round(value)}%`)
    }

    bullet.get('sprite').on('rotation', updateLabel)
    resumenChart.value._gauge = { axisDataItem, chart, updateLabel }
    chart.appear(1000, 100)
  }

  const updateGauge = async (value) => {
    await initGauge()
    const gauge = resumenChart.value?._gauge
    if (!gauge) return
    const clamped = Math.max(0, Math.min(150, value))
    gauge.axisDataItem.animate({
      key: 'value',
      to: clamped,
      duration: 500,
      easing: am5.ease.out(am5.ease.cubic),
    })
    gauge.updateLabel()
  }

  return {
    pagosPorMes,
      detallePorMes,
      totalPagos,
      totalGastos,
      totalMatriculas,
      cantidadPagos,
      cantidadGastos,
      cantidadMatriculas,
      utilidad,
      fechaInicio,
      fechaFin,
      loading,
      error,
    pagosChart,
    resumenChart,
    cargarPagos,
    formatCurrency,
  }
},
})
</script>

<style scoped>
.gap-3 {
  gap: 12px;
}

.chart-panel {
  min-height: 320px;
}

.amchart {
  width: 100%;
  height: 360px;
}
</style>
